import * as Sentry from '@sentry/react';
import jwtDecode from 'jwt-decode';
import { apiService, ApiService } from 'services';
import { ACCOUNTS_ENV, ACCOUNTS_PARTNER } from 'settings';
import { userActions } from 'store/features';
import { renderToastMessageApi } from 'utils/toastMessage';

import store from '../store';
import { IAuth, IUser } from '../types/interfaces';
import { analyticsG4 } from './analyticsG4';
import { cookieService } from './cookieService';
import eduzzToolbar from './eduzzToolbar';

const LOGGED_USER_ACCOUNT_ID = 'sun_admin_logged_user_id';
class AuthService {
  constructor(private apiService: ApiService) {
    this.apiService = apiService;
    this.login();
  }

  public setToken({ token, refresh }: IAuth) {
    const userTokenData = this.decodeUserToken(token);
    store.dispatch(userActions.setUser({ token, refresh, ...userTokenData }));
  }

  public logout() {
    cookieService.removeCookie(LOGGED_USER_ACCOUNT_ID);
    cookieService.removeCookie('isRedirected');

    window.history.pushState(null, 'Checkout Sun | Admin', '/');
    eduzzToolbar.clearUser();

    window.Eduzz.Accounts.logout({
      env: ACCOUNTS_ENV
    });
  }

  private login() {
    window.Eduzz.Accounts.login(ACCOUNTS_PARTNER, {
      env: ACCOUNTS_ENV
    })
      .switchMap((token: string) => this.apiService.post('/auth/login', { token }))
      .catch(() => store.dispatch(userActions.isLogged(false)))
      .subscribe((user: IUser) => {
        const userTokenData = this.decodeUserToken(user.token);
        store.dispatch(
          userActions.setUser({
            ...user,
            ...userTokenData,
            isLogged: true
          })
        );
        eduzzToolbar.setUser();

        Sentry.setUser({
          id: String(user.id),
          name: user.name,
          email: user.email
        });
        analyticsG4.config();
      });
  }

  public async switchAccount(userIdToAccess: number) {
    try {
      store.dispatch(userActions.setIsLoadingAccount(true));
      const user = await this.apiService.post('/auth/switchaccount', { userIdToAccess });
      const userTokenData = this.decodeUserToken(user.token);
      this.setUserIdCookie(String(userTokenData.id));
      store.dispatch(
        userActions.setUser({
          ...user,
          ...userTokenData,
          isLogged: true
        })
      );
      store.dispatch(userActions.setIsLoadingAccount(false));
    } catch (err) {
      if (err?.status === 403) {
        this.logout();
      }
      renderToastMessageApi(401, 'Permissão negada');
      store.dispatch(userActions.setIsLoadingAccount(false));
      throw new Error();
    }
  }

  public setUserIdCookie(id: string) {
    cookieService.setCookie(LOGGED_USER_ACCOUNT_ID, id);
  }

  public getUserIdCookie() {
    return cookieService.getCookie(LOGGED_USER_ACCOUNT_ID);
  }

  private decodeUserToken(token: string): Partial<IUser> {
    try {
      return jwtDecode(token);
    } catch (err) {
      return {};
    }
  }
}

export { AuthService };
export default new AuthService(apiService);
